import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Plus, ExternalLink, Lock, Trash2, FlaskConical, CheckCircle2, XCircle, Pencil, FolderPlus, Star, TrendingUp, Award, Send, FileCheck2, User, Clock } from "lucide-react";
import { useGlobalViewMode } from "./ViewModeToggle";

const STATUS_COLOR = {
  Submitted: "bg-blue-100 text-blue-700",
  "Under Test": "bg-amber-100 text-amber-800",
  "Needs Fix": "bg-rose-100 text-rose-700",
  Completed: "bg-emerald-100 text-emerald-700",
};

// v107 Part 9 — Submit form dropdowns
const LEVEL_OPTIONS = ["Beginner", "Intermediate", "Expert"];
const TAG_OPTIONS = ["AI Based", "IoT Based"];
const DELIVERABLE_OPTIONS = [
  "Document",
  "Project Library",
  "Code",
  "Video Of the Project",
  "Components Images",
];

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
  } catch { return s; }
};

export default function TrainerProjectsTab({ trainerList, canApprove }) {
  const { user } = useAuth();
  const isAdmin = ["master_admin", "sales_admin", "crm_manager"].includes(user?.role);
  const isMaster = user?.role === "master_admin";
  const isTrainer = user?.role === "trainer";

  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty());
  const [editing, setEditing] = useState(null); // project being edited (rework or admin override)
  const [reviewing, setReviewing] = useState(null);
  // v107 Part 7 — trainer stats + admin rating dialog
  const [stats, setStats] = useState(null);
  const [ratingRow, setRatingRow] = useState(null);
  const [ratingVal, setRatingVal] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get("/trainer-projects");
      setProjects(data || []);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    try {
      const { data } = await api.get("/trainer-projects/my-stats");
      setStats(data || null);
    } catch { setStats(null); }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.project_name.trim()) { toast.error("Project name required"); return; }
    try {
      await api.post("/trainer-projects", form);
      toast.success("Project submitted");
      setShowForm(false); setForm(empty()); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const saveEdit = async (resubmit = false) => {
    if (!editing) return;
    if (!editing.project_name?.trim()) { toast.error("Project name required"); return; }
    try {
      const payload = {
        project_name: editing.project_name,
        project_objective: editing.project_objective,
        document_url: editing.document_url,
        project_outcome: editing.project_outcome,
        is_different_from_56: editing.is_different_from_56,
      };
      if (resubmit) payload.resubmit = true;
      await api.patch(`/trainer-projects/${editing.id}`, payload);
      toast.success(resubmit ? "Reworked & resubmitted ✓" : "Project updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const assignTester = async (p, testerId, testerName) => {
    try {
      await api.patch(`/trainer-projects/${p.id}`, { tester_id: testerId, tester_name: testerName });
      toast.success("Tester assigned"); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const submitReview = async () => {
    try {
      await api.patch(`/trainer-projects/${reviewing.id}`, {
        tester_review: reviewing._review,
        has_errors: reviewing._has_errors,
      });
      toast.success("Review submitted"); setReviewing(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const markCompleted = async (p) => {
    if (!window.confirm("Mark project as Completed? It will be locked.")) return;
    try {
      await api.patch(`/trainer-projects/${p.id}`, { status: "Completed" });
      toast.success("Project completed and locked"); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm("Delete this project permanently?")) return;
    try { await api.delete(`/trainer-projects/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // v107 — Approval workflow
  const approveProject = async (p) => {
    try { await api.post(`/trainer-projects/${p.id}/approve`, {}); toast.success("Project approved"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const rejectProject = async (p) => {
    const reason = window.prompt(`Reject project "${p.project_name}"? Enter reason:`);
    if (!reason || !reason.trim()) { toast.error("Reason is required"); return; }
    try { await api.post(`/trainer-projects/${p.id}/reject`, { reason: reason.trim() }); toast.success("Rejected"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // v107 Part 10 — approver pool (MA + tab_access.approval) for the reassign dropdown
  const [approverPool, setApproverPool] = useState([]);
  useEffect(() => {
    if (!isMaster) return;
    // Reuse the trainers config's all_employees list as the pool (MA + everyone).
    // Practically, MA is the only one who reassigns and any active user is eligible.
    api.get("/trainers/config").then(({ data }) => {
      setApproverPool((data.all_employees || []).filter((p) => p.id !== user?.id));
    }).catch(() => {});
  }, [isMaster, user?.id]);

  const reassignApprover = async (p, newUid) => {
    try {
      await api.patch(`/trainer-projects/${p.id}/assign-approver`, { assigned_approver_id: newUid });
      toast.success(newUid ? "Approver reassigned" : "Approver cleared");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // v107 Part 10 — On-demand Drive folder creation
  const createDriveFolder = async (p) => {
    try {
      await api.post(`/trainer-projects/${p.id}/drive-folder`);
      toast.success("Google Drive folder created");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // v107 Part 7 — Admin star rating
  const openRating = (p) => { setRatingRow(p); setRatingVal(p.admin_rating || 0); };
  const submitRating = async () => {
    if (!ratingRow || ratingVal < 1) { toast.error("Pick a star (1..5)"); return; }
    try {
      await api.post(`/trainer-projects/${ratingRow.id}/rate`, { rating: ratingVal });
      toast.success("Rating submitted");
      setRatingRow(null); setRatingVal(0);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // v107 Part 8 — subscribes to the global top-bar Card/List toggle.
  const [viewMode] = useGlobalViewMode();
  const trainers = useMemo(() => (trainerList || []).filter((t) => t.id !== user?.id), [trainerList, user?.id]);

  return (
    <div className="space-y-4">
      {/* v107 Part 8 — Trainer stat tiles */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="tp-stats">
          <StatTile icon={Send} label="Submitted" value={stats.submitted_count} tone="indigo" testid="tp-stat-submitted" />
          <StatTile icon={FileCheck2} label="Approved" value={stats.approved_count} tone="emerald" testid="tp-stat-approved" />
          <StatTile icon={Clock} label="Training Days" value={stats.training_days_done} hint="Completed" tone="sky" testid="tp-stat-training-days" />
          <StatTile
            icon={TrendingUp}
            label="Rank"
            value={stats.rank > 0 ? `#${stats.rank}` : "—"}
            hint={stats.total_trainers > 0 ? `of ${stats.total_trainers} · score ${stats.rank_score}` : ""}
            tone="violet"
            testid="tp-stat-rank"
          />
          <StatTile
            icon={Star}
            label="Stars Earned"
            value={stats.max_possible_stars > 0 ? `${stats.total_stars_earned} / ${stats.max_possible_stars}` : "—"}
            hint={stats.total_ratings_given > 0 ? `${stats.total_ratings_given} rated` : "Projects + trainings"}
            tone="amber"
            testid="tp-stat-rating"
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12px] text-slate-600 dark:text-slate-300">
          <span className="font-semibold text-amber-700 dark:text-amber-300">Upload every checklist item to the Drive folder</span> — the more complete your submission, the more stars you&apos;ll earn.
        </div>
        <Button onClick={() => { setForm(empty()); setShowForm(true); }} className="!bg-indigo-600 hover:!bg-indigo-700 !text-white h-10" data-testid="tp-new" data-keep-color>
          <Plus size={14} className="mr-1.5" /> Submit Project
        </Button>
      </div>

      {/* v107 Part 8 — List (streamlined columns) OR Card view, driven by top-bar toggle */}
      {viewMode === "list" ? (
        <div className="pk-card overflow-x-auto">
          <table className="w-full text-sm responsive-table">
            <thead className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
              <tr>
                <th className="px-4 py-3 text-left pk-table-header w-[130px]">Code</th>
                <th className="px-4 py-3 text-left pk-table-header">Project Name</th>
                <th className="px-4 py-3 text-left pk-table-header w-[210px]">Submitted By</th>
                <th className="px-4 py-3 text-left pk-table-header w-[160px]">Approver</th>
                <th className="px-4 py-3 text-left pk-table-header w-[110px]">Status</th>
                <th className="px-4 py-3 text-center pk-table-header w-[130px]">Drive</th>
                <th className="px-4 py-3 text-right pk-table-header w-[260px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No projects yet.</td></tr>}
              {projects.map((p) => {
                const isCreator = p.created_by_id === user?.id;
                const isTester = p.tester_id === user?.id;
                const approvalPending = p.approval_status === "pending";
                const approvalRejected = p.approval_status === "rejected";
                return (
                  <tr key={p.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/60 dark:hover:bg-slate-900/40" data-testid={`tp-row-${p.id}`}>
                    <td className="px-4 py-3 mono text-[11.5px] font-semibold text-indigo-700 dark:text-indigo-300 whitespace-nowrap" data-label="Code">
                      {p.project_code || `#${p.serial_no || "—"}`}
                    </td>
                    <td className="px-4 py-3" data-label="Project Name">
                      <div className="font-semibold text-slate-900 dark:text-slate-100 truncate max-w-[280px] flex items-center gap-1.5">
                        {p.project_name}
                        {p.locked && <Lock size={11} className="text-slate-400 shrink-0" />}
                      </div>
                      {p.project_objective && <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-[280px]">{p.project_objective}</div>}
                      {(p.level || (p.tags || []).length > 0) && (
                        <div className="flex flex-wrap items-center gap-1 mt-1.5">
                          {p.level && <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10 ring-1 ring-indigo-200 dark:ring-indigo-500/30 rounded px-1.5 py-0.5">{p.level}</span>}
                          {(p.tags || []).slice(0, 3).map((t) => (
                            <span key={t} className="text-[10px] font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded px-1.5 py-0.5">{t}</span>
                          ))}
                          {(p.tags || []).length > 3 && <span className="text-[10px] text-slate-500">+{p.tags.length - 3}</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3" data-label="Submitted By">
                      <div className="text-[12px] font-medium text-slate-800 dark:text-slate-100 flex items-center gap-1"><User size={11} className="text-slate-400" /> {p.created_by_name}</div>
                      <div className="text-[10px] mono text-slate-500 flex items-center gap-1"><Clock size={9} /> {fmtDate(p.created_at)}</div>
                    </td>
                    <td className="px-4 py-3" data-label="Approver">
                      {p.assigned_approver_name || p.approved_by_name ? (
                        <>
                          <div className="text-[12px] font-medium text-slate-800 dark:text-slate-100 truncate">{p.approved_by_name || p.assigned_approver_name}</div>
                          <div className="text-[10px] mono text-slate-500">{p.approved_at ? fmtDate(p.approved_at) : (p.approver_action_at ? `Approver acted · ${fmtDate(p.approver_action_at)}` : "Awaiting action")}</div>
                          {p.is_auto_approved && <Badge className="bg-violet-50 text-violet-700 ring-1 ring-violet-200 text-[9px] font-semibold mt-0.5" data-keep-color>Auto-approved</Badge>}
                        </>
                      ) : (
                        <span className="text-[11px] italic text-slate-400">Awaiting approval</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center" data-label="Status">
                      <Badge
                        className={`text-[10px] font-semibold whitespace-nowrap ${approvalPending ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200" : approvalRejected ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"}`}
                        data-keep-color
                      >
                        {approvalPending ? "Pending" : approvalRejected ? "Rejected" : "Approved"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center" data-label="Drive">
                      {p.drive_folder_url ? (
                        <a href={p.drive_folder_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11.5px] text-emerald-700 dark:text-emerald-300 hover:underline" data-testid={`tp-drive-${p.id}`}>
                          <ExternalLink size={11} /> Open
                        </a>
                      ) : (
                        <Button onClick={() => createDriveFolder(p)} size="sm" variant="outline" className="h-7 text-[10.5px] px-2" data-testid={`tp-drive-create-${p.id}`}>
                          <FolderPlus size={10} className="mr-1" /> Create
                        </Button>
                      )}
                    </td>
                    <td className="px-4 py-3" data-label="Actions">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        <RowActions
                          p={p}
                          isAdmin={isAdmin}
                          isMaster={isMaster}
                          canApprove={canApprove}
                          isCreator={isCreator}
                          isTester={isTester}
                          approvalPending={approvalPending}
                          onApprove={approveProject}
                          onReject={rejectProject}
                          onRate={openRating}
                          onAssignTester={assignTester}
                          trainers={trainers}
                          onReview={(row) => setReviewing({ ...row, _review: row.tester_review || "", _has_errors: row.status === "Needs Fix" })}
                          onRework={(row) => setEditing({ ...row })}
                          onMarkCompleted={markCompleted}
                          onEdit={(row) => setEditing({ ...row })}
                          onRemove={remove}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        // ==================== CARD VIEW ====================
        <>
          {projects.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No projects yet.</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {projects.map((p) => {
              const isCreator = p.created_by_id === user?.id;
              const isTester = p.tester_id === user?.id;
              const approvalPending = p.approval_status === "pending";
              const approvalRejected = p.approval_status === "rejected";
              const statusChip = approvalPending ? { cls: "bg-amber-50 text-amber-700 ring-amber-200", label: "Pending" }
                : approvalRejected ? { cls: "bg-rose-50 text-rose-700 ring-rose-200", label: "Rejected" }
                : { cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", label: "Approved" };
              return (
                <div key={p.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 flex flex-col gap-2.5 transition-colors hover:border-indigo-300 dark:hover:border-indigo-500/40" data-testid={`tp-card-${p.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="mono text-[11px] font-semibold text-indigo-700 dark:text-indigo-300 truncate">{p.project_code || `#${p.serial_no || "—"}`}</span>
                    <Badge className={`text-[10px] font-semibold whitespace-nowrap ring-1 ${statusChip.cls}`} data-keep-color>{statusChip.label}</Badge>
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                      <span className="truncate">{p.project_name}</span>
                      {p.locked && <Lock size={11} className="text-slate-400 shrink-0" />}
                    </div>
                    {p.project_objective && <div className="text-[11.5px] text-slate-600 dark:text-slate-300 line-clamp-2 mt-0.5">{p.project_objective}</div>}
                    {(p.level || (p.tags || []).length > 0) && (
                      <div className="flex flex-wrap items-center gap-1 mt-1.5">
                        {p.level && <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10 ring-1 ring-indigo-200 dark:ring-indigo-500/30 rounded px-1.5 py-0.5">{p.level}</span>}
                        {(p.tags || []).map((t) => (
                          <span key={t} className="text-[10px] font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded px-1.5 py-0.5">{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="border-t border-slate-100 dark:border-slate-800 pt-2 text-[11.5px] text-slate-700 dark:text-slate-200 space-y-1">
                    <div className="flex items-center gap-1.5"><User size={11} className="text-slate-400" /> <span className="font-semibold">{p.created_by_name}</span> <span className="text-slate-400">·</span> <span className="mono text-[10.5px] text-slate-500">{fmtDate(p.created_at)}</span></div>
                    <div className="flex items-center gap-1.5"><Award size={11} className="text-slate-400" /> {p.approved_by_name ? <><span className="font-semibold">{p.approved_by_name}</span> <span className="text-slate-400">·</span> <span className="mono text-[10.5px] text-slate-500">{fmtDate(p.approved_at)}</span></> : <span className="italic text-slate-400">Awaiting approval</span>}</div>
                    <div className="flex items-center gap-1.5">
                      <FolderPlus size={11} className="text-slate-400" />
                      {p.drive_folder_url ? (
                        <a href={p.drive_folder_url} target="_blank" rel="noreferrer" className="text-emerald-700 dark:text-emerald-300 hover:underline inline-flex items-center gap-0.5" data-testid={`tp-drive-${p.id}`}>
                          <ExternalLink size={10} /> Drive folder
                        </a>
                      ) : (
                        <button type="button" onClick={() => createDriveFolder(p)} className="text-slate-500 hover:text-indigo-700 dark:hover:text-indigo-300 hover:underline text-left" data-testid={`tp-drive-create-${p.id}`}>Create Drive folder</button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-1.5 flex-wrap pt-1 border-t border-slate-100 dark:border-slate-800">
                    <RowActions
                      p={p}
                      isAdmin={isAdmin}
                      isMaster={isMaster}
                      canApprove={canApprove}
                      isCreator={isCreator}
                      isTester={isTester}
                      approvalPending={approvalPending}
                      onApprove={approveProject}
                      onReject={rejectProject}
                      onRate={openRating}
                      onAssignTester={assignTester}
                      trainers={trainers}
                      onReview={(row) => setReviewing({ ...row, _review: row.tester_review || "", _has_errors: row.status === "Needs Fix" })}
                      onRework={(row) => setEditing({ ...row })}
                      onMarkCompleted={markCompleted}
                      onEdit={(row) => setEditing({ ...row })}
                      onRemove={remove}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}


      {/* v107 Part 7 — Redesigned Submit Project / Activity Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="tp-form-dialog">
          <div className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-800 flex items-center justify-center">
                <Award size={18} className="text-indigo-700 dark:text-indigo-300" />
              </div>
              <div>
                <DialogTitle className="heading text-base font-bold text-slate-900 dark:text-slate-100">Submit New Project / Activity</DialogTitle>
                <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">Auto-assigned a Project Code, Serial No. and an optional Google Drive folder. Master Admin will approve before it becomes visible.</div>
              </div>
            </div>
          </div>
          <div className="px-6 py-4 space-y-4">
            {/* Section 1 · Basics */}
            <SectionBox title="Project Basics">
              <Lbl label="Project Name *"><Input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} placeholder="e.g. Solar-Panel-Powered IoT Weather Station" data-testid="tp-name" /></Lbl>
              <Lbl label="Objective / Learning Outcome"><Textarea rows={3} value={form.project_objective} onChange={(e) => setForm({ ...form, project_objective: e.target.value })} placeholder="What does this project teach? Why is it a good add to the 56?" data-testid="tp-objective" /></Lbl>
            </SectionBox>

            {/* v107 Part 9 · Section 2 · Google Drive helper — no more free-text deliverables */}
            <SectionBox title="Google Drive Uploads">
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10 px-3.5 py-3 flex items-start gap-3">
                <div className="w-9 h-9 rounded-md bg-white dark:bg-slate-950 ring-1 ring-emerald-200 dark:ring-emerald-500/30 flex items-center justify-center shrink-0">
                  <FolderPlus size={16} className="text-emerald-700 dark:text-emerald-300" />
                </div>
                <div className="text-[11.5px] text-slate-700 dark:text-slate-200">
                  <div className="font-semibold text-slate-900 dark:text-slate-100 mb-0.5">A Google Drive folder will be created on submit.</div>
                  Please plan to upload the following to that folder (tick everything you intend to include so the reviewer knows what to expect):
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                {DELIVERABLE_OPTIONS.map((opt) => {
                  const checked = form.deliverables.includes(opt);
                  return (
                    <label key={opt} className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer transition-colors ${checked ? "border-indigo-300 dark:border-indigo-500/50 bg-indigo-50/60 dark:bg-indigo-500/10" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 hover:border-slate-300"}`} data-testid={`tp-deliv-${opt.replace(/\s+/g, "-").toLowerCase()}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setForm((f) => ({
                            ...f,
                            deliverables: checked ? f.deliverables.filter((x) => x !== opt) : [...f.deliverables, opt],
                          }));
                        }}
                        className="h-4 w-4 accent-indigo-600"
                      />
                      <span className="text-[12.5px] font-medium text-slate-800 dark:text-slate-100">{opt}</span>
                    </label>
                  );
                })}
              </div>
              <Lbl label="Backup Document URL (optional)">
                <Input value={form.document_url} onChange={(e) => setForm({ ...form, document_url: e.target.value })} placeholder="https://… (if you have a public link outside Drive)" data-testid="tp-doc" />
              </Lbl>
            </SectionBox>

            {/* v107 Part 9 · Section 3 · Classification (level + tags) */}
            <SectionBox title="Classification">
              <Lbl label="Difficulty Level *">
                <div className="flex flex-wrap gap-2">
                  {LEVEL_OPTIONS.map((lv) => {
                    const active = form.level === lv;
                    return (
                      <button
                        key={lv}
                        type="button"
                        onClick={() => setForm({ ...form, level: lv })}
                        className={`px-3 h-9 rounded-md text-[12px] font-semibold transition-colors ring-1 ${active ? "!bg-indigo-600 !text-white !ring-indigo-700" : "bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 ring-slate-200 dark:ring-slate-800 hover:ring-indigo-300"}`}
                        data-testid={`tp-level-${lv.toLowerCase()}`}
                        data-keep-color={active ? "" : undefined}
                      >
                        {lv}
                      </button>
                    );
                  })}
                </div>
              </Lbl>
              <Lbl label="Tags (pick one or more)">
                <div className="flex flex-wrap gap-2 mb-2">
                  {TAG_OPTIONS.map((tg) => {
                    const active = form.tags.includes(tg);
                    return (
                      <button
                        key={tg}
                        type="button"
                        onClick={() => {
                          setForm((f) => ({ ...f, tags: active ? f.tags.filter((t) => t !== tg) : [...f.tags, tg] }));
                        }}
                        className={`px-3 h-8 rounded-full text-[11.5px] font-semibold transition-colors ring-1 ${active ? "!bg-indigo-600 !text-white !ring-indigo-700" : "bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 ring-slate-200 dark:ring-slate-800 hover:ring-indigo-300"}`}
                        data-testid={`tp-tag-${tg.replace(/\s+/g, "-").toLowerCase()}`}
                        data-keep-color={active ? "" : undefined}
                      >
                        {tg}
                      </button>
                    );
                  })}
                </div>
                <CustomTagInput
                  extras={form.tags.filter((t) => !TAG_OPTIONS.includes(t))}
                  onAdd={(txt) => setForm((f) => ({ ...f, tags: [...new Set([...f.tags, txt])] }))}
                  onRemove={(txt) => setForm((f) => ({ ...f, tags: f.tags.filter((t) => t !== txt) }))}
                />
              </Lbl>
              <div className="flex items-center gap-3 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
                <Switch checked={form.is_different_from_56} onCheckedChange={(v) => setForm({ ...form, is_different_from_56: v })} data-testid="tp-diff" />
                <div>
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">Different from the OLD &amp; NEW 56 projects?</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">Flag this if the project doesn&apos;t overlap with the standard 56.</div>
                </div>
              </div>
            </SectionBox>
          </div>
          <DialogFooter className="px-6 pb-5">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={submit} className="!bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid="tp-submit" data-keep-color>
              <Send size={13} className="mr-1.5" /> Submit for Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v107 Part 7 — Rating Dialog (approver awards 1..5 stars) */}
      <Dialog open={!!ratingRow} onOpenChange={(o) => !o && setRatingRow(null)}>
        <DialogContent className="max-w-sm" data-testid="tp-rating-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <Star size={16} className="text-amber-500 fill-amber-400" /> Rate Project
            </DialogTitle>
            <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">{ratingRow?.project_code} · {ratingRow?.project_name}</div>
          </DialogHeader>
          <div className="py-4 flex items-center justify-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRatingVal(n)}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`tp-rating-${n}`}
                aria-label={`${n} star${n === 1 ? "" : "s"}`}
              >
                <Star size={32} className={n <= ratingVal ? "text-amber-500 fill-amber-400" : "text-slate-300 dark:text-slate-600"} />
              </button>
            ))}
          </div>
          <div className="text-center text-[11.5px] text-slate-500 dark:text-slate-400 mb-2">
            {ratingVal > 0 ? `${ratingVal} out of 5` : "Pick a rating"}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRatingRow(null)}>Cancel</Button>
            <Button onClick={submitRating} className="!bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid="tp-rating-submit" data-keep-color>Save Rating</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit / Rework dialog (Trainer-creator rework OR Master Admin override) */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="tp-edit-dialog">
          <DialogHeader>
            <DialogTitle className="heading">
              {editing?.status === "Needs Fix" && !isMaster ? "Rework & Resubmit Project" : "Edit Project"}
            </DialogTitle>
            {editing?.tester_review && (
              <div className="mt-2 border border-rose-200 bg-rose-50 rounded-md p-3" data-testid="tp-prev-review">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-700">Tester Feedback</div>
                <div className="text-[12px] text-slate-700 mt-1 italic">&quot;{editing.tester_review}&quot;</div>
                {editing.tester_name && <div className="text-[10px] text-slate-500 mt-1">— {editing.tester_name}</div>}
              </div>
            )}
          </DialogHeader>
          <div className="py-2 space-y-3">
            <Lbl label="Project Name *"><Input value={editing?.project_name || ""} onChange={(e) => setEditing({ ...editing, project_name: e.target.value })} data-testid="tp-edit-name" /></Lbl>
            <Lbl label="Project Objective"><Textarea rows={2} value={editing?.project_objective || ""} onChange={(e) => setEditing({ ...editing, project_objective: e.target.value })} data-testid="tp-edit-objective" /></Lbl>
            <Lbl label="Document URL"><Input value={editing?.document_url || ""} onChange={(e) => setEditing({ ...editing, document_url: e.target.value })} placeholder="https://…" data-testid="tp-edit-doc" /></Lbl>
            <Lbl label="Project Outcome"><Textarea rows={2} value={editing?.project_outcome || ""} onChange={(e) => setEditing({ ...editing, project_outcome: e.target.value })} data-testid="tp-edit-outcome" /></Lbl>
            <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md">
              <Switch checked={!!editing?.is_different_from_56} onCheckedChange={(v) => setEditing({ ...editing, is_different_from_56: v })} data-testid="tp-edit-diff" />
              <span className="text-sm text-slate-800">Is this project different from the OLD & NEW 56 projects?</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            {editing?.status === "Needs Fix" && !isMaster ? (
              <>
                <Button variant="outline" onClick={() => saveEdit(false)} data-testid="tp-edit-save">Save Draft</Button>
                <Button onClick={() => saveEdit(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="tp-edit-resubmit">
                  Save & Resubmit for Re-Test
                </Button>
              </>
            ) : (
              <Button onClick={() => saveEdit(false)} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="tp-edit-save">Save Changes</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="tp-review-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Test Project — {reviewing?.project_name}</DialogTitle>
            <div className="text-[11px] text-slate-500 mt-1">By {reviewing?.created_by_name} · {fmtDate(reviewing?.created_at)}</div>
          </DialogHeader>
          <div className="py-2 space-y-3">
            {/* Read-only project details */}
            <div className="border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50/50">
              <div className="pk-table-header">Project Details</div>
              <KV k="Objective" v={reviewing?.project_objective || "—"} />
              <KV k="Document" v={reviewing?.document_url ? <a href={reviewing.document_url} target="_blank" rel="noreferrer" className="text-[var(--pk-accent)] hover:underline inline-flex items-center gap-1"><ExternalLink size={10} /> Open</a> : "—"} />
              <KV k="Outcome" v={reviewing?.project_outcome || "—"} />
              <KV k="Different from 56?" v={reviewing?.is_different_from_56 ? "Yes" : "No"} />
            </div>
            {/* Tester input */}
            <Lbl label="Review / Findings *">
              <Textarea rows={5} value={reviewing?._review || ""} onChange={(e) => setReviewing({ ...reviewing, _review: e.target.value })} data-testid="tp-review-text" placeholder="What did you test, what passed, what failed?" />
            </Lbl>
            <div className="flex items-center gap-3 px-3 py-2 bg-rose-50 border border-rose-200 rounded-md">
              <Switch checked={!!reviewing?._has_errors} onCheckedChange={(v) => setReviewing({ ...reviewing, _has_errors: v })} data-testid="tp-review-has-errors" />
              <span className="text-sm text-slate-800">Errors found — developer needs to fix?</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewing(null)}>Cancel</Button>
            <Button onClick={submitReview} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="tp-review-submit">Submit Review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-start gap-2 text-[12px]">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-32 shrink-0 mt-0.5">{k}</span>
      <span className="font-medium text-slate-800 flex-1">{v}</span>
    </div>
  );
}

// v107 Part 7 — Section wrapper used inside the redesigned Submit dialog.
function SectionBox({ title, children }) {
  return (
    <section className="space-y-3">
      <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-indigo-700 dark:text-indigo-300">{title}</div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

// v107 Part 7 — Stat tile at the top of the Project & Activity tab.
// v107 Part 10 — Icon-only action buttons with fixed color palette
// Reject: red · Approve: green · Edit: blue · Delete: orange
function IconBtn({ onClick, tone, title, testid, children, disabled }) {
  const toneMap = {
    green: "text-emerald-600 border-emerald-200 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-500/30 dark:hover:bg-emerald-500/10",
    red: "text-rose-600 border-rose-200 hover:bg-rose-50 dark:text-rose-400 dark:border-rose-500/30 dark:hover:bg-rose-500/10",
    blue: "text-blue-600 border-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-500/30 dark:hover:bg-blue-500/10",
    orange: "text-orange-600 border-orange-200 hover:bg-orange-50 dark:text-orange-400 dark:border-orange-500/30 dark:hover:bg-orange-500/10",
    slate: "text-slate-600 border-slate-200 hover:bg-slate-50 dark:text-slate-300 dark:border-slate-700 dark:hover:bg-slate-800",
    amber: "text-amber-600 border-amber-200 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-500/30 dark:hover:bg-amber-500/10",
  };
  return (
    <Button onClick={onClick} disabled={disabled} size="sm" variant="outline" title={title} data-testid={testid}
      className={`h-8 w-8 p-0 shrink-0 border ${toneMap[tone] || toneMap.slate}`}>
      {children}
    </Button>
  );
}


// v107 Part 8 — Shared action buttons for both List and Card views. Keeps
// symmetric alignment and identical role gating across both layouts.
function RowActions({ p, isAdmin, isMaster, canApprove, isCreator, isTester, approvalPending, onApprove, onReject, onRate, onAssignTester, trainers, onReview, onRework, onMarkCompleted, onEdit, onRemove }) {
  const isAssignedApprover = p.assigned_approver_id && !isMaster; // regular assigned approver
  const isApproved = p.approval_status === "approved";
  // v109 — Post-approval, only Master Admin can Edit / Delete a project.
  const canEdit = isMaster || (!isApproved && (isMaster || isCreator));
  const canDelete = isMaster || (!isApproved && isAdmin);
  return (
    <>
      {canApprove && p.approval_status === "approved" && (
        <IconBtn onClick={() => onRate(p)} tone="amber" title={p.admin_rating ? `Rating: ${p.admin_rating}★` : "Rate"} testid={`tp-rate-${p.id}`}>
          <Star size={13} className={p.admin_rating ? "text-amber-500 fill-amber-400" : ""} />
        </IconBtn>
      )}
      {canApprove && approvalPending && (
        <>
          <IconBtn onClick={() => onApprove(p)} tone="green" title={isAssignedApprover ? "Approve (awaits Master Admin ack · 24h auto)" : "Approve"} testid={`tp-approve-${p.id}`}>
            <CheckCircle2 size={13} />
          </IconBtn>
          <IconBtn onClick={() => onReject(p)} tone="red" title="Reject" testid={`tp-reject-${p.id}`}>
            <XCircle size={13} />
          </IconBtn>
        </>
      )}
      {isTester && !p.locked && (
        <IconBtn onClick={() => onReview(p)} tone="slate" title="Review" testid={`tp-review-${p.id}`}>
          <FlaskConical size={13} />
        </IconBtn>
      )}
      {isCreator && !p.locked && p.status === "Needs Fix" && (
        <IconBtn onClick={() => onRework(p)} tone="amber" title="Rework & resubmit" testid={`tp-rework-${p.id}`}>
          <Pencil size={13} />
        </IconBtn>
      )}
      {isCreator && !p.locked && p.status !== "Completed" && p.tester_review && p.status !== "Needs Fix" && (
        <IconBtn onClick={() => onMarkCompleted(p)} tone="green" title="Mark completed" testid={`tp-done-${p.id}`}>
          <CheckCircle2 size={13} />
        </IconBtn>
      )}
      {canEdit && (
        <IconBtn onClick={() => onEdit(p)} tone="blue" title={isApproved ? "Edit (Master Admin)" : "Edit"} testid={`tp-edit-${p.id}`}>
          <Pencil size={13} />
        </IconBtn>
      )}
      {canDelete && (
        <IconBtn onClick={() => onRemove(p)} tone="orange" title={isApproved ? "Delete (Master Admin)" : "Delete"} testid={`tp-del-${p.id}`}>
          <Trash2 size={13} />
        </IconBtn>
      )}
    </>
  );
}

function StatTile({ icon: Icon, label, value, hint, tone = "indigo", testid }) {
  const toneMap = {
    indigo: "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 ring-indigo-200 dark:ring-indigo-500/30",
    emerald: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-500/30",
    violet: "bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 ring-violet-200 dark:ring-violet-500/30",
    amber: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-amber-200 dark:ring-amber-500/30",
    sky: "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-sky-200 dark:ring-sky-500/30",
  };
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-4 py-3 flex items-center gap-3" data-testid={testid}>
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ring-1 ${toneMap[tone] || toneMap.indigo}`}>
        <Icon size={16} strokeWidth={2.25} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{label}</div>
        <div className="text-lg font-bold text-slate-900 dark:text-slate-100 mono leading-tight">{value ?? "—"}</div>
        {hint && <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function empty() {
  return {
    project_name: "",
    project_objective: "",
    document_url: "",
    project_outcome: "",
    is_different_from_56: false,
    // v107 Part 9 — new fields
    level: "",
    tags: [],
    deliverables: [],
  };
}

function Lbl({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

// v107 Part 9 — Custom tag input for user-entered tags beyond the presets.
function CustomTagInput({ extras, onAdd, onRemove }) {
  const [v, setV] = useState("");
  const commit = () => {
    const t = v.trim();
    if (!t) return;
    onAdd(t);
    setV("");
  };
  return (
    <div>
      <div className="flex items-center gap-2">
        <Input
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
          placeholder="Add your own tag (press Enter)…"
          className="h-9"
          data-testid="tp-tag-custom-input"
        />
        <Button type="button" variant="outline" onClick={commit} className="h-9 shrink-0" data-testid="tp-tag-custom-add">Add</Button>
      </div>
      {extras.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {extras.map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[11px] font-medium">
              {t}
              <button type="button" onClick={() => onRemove(t)} className="text-slate-400 hover:text-rose-600" data-testid={`tp-tag-custom-x-${t.replace(/\s+/g, "-").toLowerCase()}`}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
